// Column filter constants based on SAP audit data structure
export const AUDIT_LOG_COLUMN_GROUPS = {
  SEC_AUDIT_LOG: 'sec_audit_log',
  CHANGE_LOG: 'change_log',
  TCODE_LOG: 'tcode_log',
  SYSTEM_LOG: 'system_log',
} as const;

// Fixed columns that cannot be unchecked (timestamp and title for each log type)
export const FIXED_AUDIT_COLUMNS = [
  'SEC_AUDIT_LOG-timestamp',
  'SEC_AUDIT_LOG-title',
  'CHANGE_LOG-timestamp',
  'CHANGE_LOG-title',
  'TCODE_LOG-timestamp',
  'TCODE_LOG-title',
  'SYSTEM_LOG-timestamp',
  'SYSTEM_LOG-title',
];

// Priority columns order (higher priority first) based on Figma design
export const PRIORITY_AUDIT_COLUMNS = [
  'SEC_AUDIT_LOG-logType',
  'SEC_AUDIT_LOG-tcodeDescription',
  'SEC_AUDIT_LOG-tcodeFunctionArea',
  'SEC_AUDIT_LOG-auditClass',
  'SEC_AUDIT_LOG-severity',
  'SEC_AUDIT_LOG-tcodeValue',

  // Change Log priorities
  // Reverse-listed to render desired order (1..10 as specified)
  'CHANGE_LOG-shortText',
  'CHANGE_LOG-field',
  'CHANGE_LOG-table',
  'CHANGE_LOG-tcodeFunctionArea',
  'CHANGE_LOG-logType',
  'CHANGE_LOG-tcodeDescription',
  'CHANGE_LOG-oldValue',
  'CHANGE_LOG-newValue',
  'CHANGE_LOG-changeIndicator',
  'CHANGE_LOG-tcodeValue',

  // Entitlement Log priorities
  // Reverse-listed to render desired order for Entitlement Log:
  // 1) Entitlement, 2) Entitlement Function Area, 3) Usage Period, 4) Usage Value,
  // 5) Entitlement Description, 6) Usage Start Date, 7) Usage End Date
  'TCODE_LOG-usageEndDate',
  'TCODE_LOG-usageStartDate',
  'TCODE_LOG-tcodeDescription',
  'TCODE_LOG-usageValue',
  'TCODE_LOG-usagePeriod',
  'TCODE_LOG-tcodeFunctionArea',
  'TCODE_LOG-logType',
  'TCODE_LOG-tcodeValue',

  // System Log priorities
  // Reverse-listed to render desired order for System Log:
  // 1) Entitlement, 2) Severity, 3) Entitlement Function Area, 4) Entitlement Description
  'SYSTEM_LOG-tcodeDescription',
  'SYSTEM_LOG-tcodeFunctionArea',
  'SYSTEM_LOG-severity',
  'SYSTEM_LOG-logType',
  'SYSTEM_LOG-tcodeValue',

  // System Log priorities
  'SYSTEM_LOG-timestamp',
  'SYSTEM_LOG-title',
  'SYSTEM_LOG-instanceName',
  'SYSTEM_LOG-clientName',
  'SYSTEM_LOG-terminalName',
  'SYSTEM_LOG-hostName',
  'SYSTEM_LOG-programName',
];

// Column group order
export const AUDIT_COLUMN_GROUP_ORDER = [
  'sec_audit_log',
  'change_log',
  'tcode_log',
  'system_log',
];

// Default column visibility based on Figma design
export const DEFAULT_VISIBLE_COLUMNS = {
  SEC_AUDIT_LOG: [
    'SEC_AUDIT_LOG-tcodeValue',
    'SEC_AUDIT_LOG-severity',
    'SEC_AUDIT_LOG-auditClass',
    'SEC_AUDIT_LOG-tcodeFunctionArea',
  ],
  CHANGE_LOG: [
    // First 4 checked as per requested order
    'CHANGE_LOG-tcodeValue',
    'CHANGE_LOG-changeIndicator',
    'CHANGE_LOG-newValue',
    'CHANGE_LOG-oldValue',
  ],
  TCODE_LOG: [
    // First 4 checked as per requested order
    'TCODE_LOG-tcodeValue',
    'TCODE_LOG-tcodeFunctionArea',
    'TCODE_LOG-usagePeriod',
    'TCODE_LOG-usageValue',
    'TCODE_LOG-logType',
  ],
  SYSTEM_LOG: [
    // All five checked as requested, including Log Type
    'SYSTEM_LOG-tcodeValue',
    'SYSTEM_LOG-severity',
    'SYSTEM_LOG-tcodeFunctionArea',
    'SYSTEM_LOG-tcodeDescription',
    'SYSTEM_LOG-logType',
  ],
};

// Column definitions with labels based on Figma design
export const AUDIT_COLUMN_DEFINITIONS = {
  // Sec Audit Log - Security and audit related information
  'SEC_AUDIT_LOG-tcodeValue': 'Entitlement',
  'SEC_AUDIT_LOG-severity': 'Severity',
  'SEC_AUDIT_LOG-auditClass': 'Audit Class',
  'SEC_AUDIT_LOG-tcodeFunctionArea': 'Entitlement Function Area',
  'SEC_AUDIT_LOG-tcodeDescription': 'Entitlement Description',
  'SEC_AUDIT_LOG-logType': 'Log Type',

  // Change Log - Change and modification information
  'CHANGE_LOG-timestamp': 'Timestamp',
  'CHANGE_LOG-title': 'Title',
  'CHANGE_LOG-tcodeValue': 'Entitlement',
  'CHANGE_LOG-tcodeDescription': 'Entitlement Description',
  'CHANGE_LOG-tcodeFunctionArea': 'Entitlement Function Area',
  'CHANGE_LOG-logType': 'Log Type',
  'CHANGE_LOG-changeIndicator': 'Change Indicator',
  'CHANGE_LOG-oldValue': 'Old Value',
  'CHANGE_LOG-newValue': 'New Value',
  'CHANGE_LOG-field': 'Field Name',
  'CHANGE_LOG-shortText': 'Field Label',
  'CHANGE_LOG-table': 'Table Name',
  'CHANGE_LOG-object': 'Object Name',
  'CHANGE_LOG-tableKey': 'Table Key',
  'CHANGE_LOG-instanceName': 'Instance',
  'CHANGE_LOG-clientName': 'Client',

  // Entitlement Log - Transaction code related information
  'TCODE_LOG-timestamp': 'Timestamp',
  'TCODE_LOG-title': 'Title',
  'TCODE_LOG-tcodeValue': 'Entitlement',
  'TCODE_LOG-tcodeDescription': 'Entitlement Description',
  'TCODE_LOG-tcodeFunctionArea': 'Entitlement Function Area',
  'TCODE_LOG-usagePeriod': 'Usage Period',
  'TCODE_LOG-usageValue': 'Usage Value',
  'TCODE_LOG-usageStartDate': 'Usage Start Date',
  'TCODE_LOG-usageEndDate': 'Usage End Date',
  'TCODE_LOG-logType': 'Log Type',
  'TCODE_LOG-tcodeSoxCritical': 'SOX Critical',
  'TCODE_LOG-tcodeSysCritical': 'System Critical',
  'TCODE_LOG-requestedTcode': 'Requested Entitlement',
  'TCODE_LOG-tcodeDeviation': 'Entitlement Deviation',
  'TCODE_LOG-instanceName': 'Instance',
  'TCODE_LOG-clientName': 'Client',
  'TCODE_LOG-userName': 'User Name',

  // System Log - System and technical information
  'SYSTEM_LOG-tcodeValue': 'Entitlement',
  'SYSTEM_LOG-severity': 'Severity',
  'SYSTEM_LOG-tcodeFunctionArea': 'Entitlement Function Area',
  'SYSTEM_LOG-tcodeDescription': 'Entitlement Description',
  'SYSTEM_LOG-logType': 'Log Type',
} as const;

// Explicit allowlist of configurable columns per group (to restrict menu options)
export const AUDIT_GROUP_AVAILABLE_COLUMNS: Record<string, string[]> = {
  SEC_AUDIT_LOG: [
    'SEC_AUDIT_LOG-tcodeValue',
    'SEC_AUDIT_LOG-severity',
    'SEC_AUDIT_LOG-auditClass',
    'SEC_AUDIT_LOG-tcodeFunctionArea',
    'SEC_AUDIT_LOG-tcodeDescription',
    'SEC_AUDIT_LOG-logType',
  ],
  CHANGE_LOG: [
    'CHANGE_LOG-tcodeValue',
    'CHANGE_LOG-changeIndicator',
    'CHANGE_LOG-newValue',
    'CHANGE_LOG-oldValue',
    'CHANGE_LOG-tcodeDescription',
    'CHANGE_LOG-logType',
    'CHANGE_LOG-tcodeFunctionArea',
    'CHANGE_LOG-table',
    'CHANGE_LOG-field',
    'CHANGE_LOG-shortText',
  ],
  TCODE_LOG: [
    'TCODE_LOG-tcodeValue',
    'TCODE_LOG-tcodeFunctionArea',
    'TCODE_LOG-usagePeriod',
    'TCODE_LOG-usageValue',
    'TCODE_LOG-tcodeDescription',
    'TCODE_LOG-usageStartDate',
    'TCODE_LOG-usageEndDate',
    'TCODE_LOG-logType',
  ],
  SYSTEM_LOG: [
    'SYSTEM_LOG-tcodeValue',
    'SYSTEM_LOG-severity',
    'SYSTEM_LOG-tcodeFunctionArea',
    'SYSTEM_LOG-tcodeDescription',
    'SYSTEM_LOG-logType',
  ],
};
